<template>
  <div>
    <v-menu
      v-if="user"
      location="bottom end"
      :offset="[8, 0]"
    >
      <template #activator="{ props: activatorProps }">
        <v-btn
          color="white"
          variant="flat"
          class="user-button"
          icon
          :size="small ? 'small' : 'default'"
          :style="!small && { width: '48px', height: '48px' }"
          elevation="2"
          v-bind="activatorProps"
        >
          <v-icon
            color="onyx"
          >
            mdi-account
          </v-icon>
        </v-btn>
      </template>
      <v-card
        width="350"
        :elevation="8"
      >
        <v-list-item class="pt-3 pb-3">
          <template #prepend>
            <v-avatar color="metal">
              <v-icon color="white">
                mdi-account
              </v-icon>
            </v-avatar>
          </template>

          <v-list-item-title class="text-h6">
            <span>{{ user.name }}</span>
            <router-link
              v-if="user.isAdmin"
              class="text-caption text-shamrock ml-1"
              to="/admin"
            >
              Admin
            </router-link>
          </v-list-item-title>
          <v-list-item-subtitle
            class="text-subtitle-1"
            style="line-height: 1;"
          >
            {{ user.company }}
          </v-list-item-subtitle>

          <template #append>
            <v-btn
              icon
              variant="text"
              color="shamrock"
              @click="editedUserInformation"
            >
              <v-icon>
                mdi-pencil
              </v-icon>
            </v-btn>
          </template>
        </v-list-item>
        <v-card-actions
          class="pt-2 d-flex flex-column"
        >
          <v-btn
            variant="text"
            color="shamrock"
            class="my-0 pl-4 justify-start"
            block
            to="/projects"
            exact
          >
            <v-icon
              start
              size="18"
            >
              mdi-nature-people
            </v-icon>
            <span>My projects</span>
          </v-btn>
          <v-btn
            variant="text"
            color="shamrock"
            class="my-0 pl-4 justify-start"
            block
            href="https://climatepositivedesign.org/wp-content/uploads/2025/10/CPD_Pathfinder-3.2_User-Guide.pdf"
            target="_blank"
          >
            <v-icon
              start
              size="18"
            >
              mdi-help
            </v-icon>
            <span>Pathfinder User Guide</span>
          </v-btn>
          <v-btn
            variant="text"
            color="shamrock"
            class="my-0 pl-4 justify-start"
            block
            href="https://climatepositivedesign.com/"
            target="_blank"
          >
            <v-icon
              start
              size="18"
            >
              mdi-web
            </v-icon>
            <span>climatepositivedesign.com</span>
          </v-btn>
          <v-btn
            v-if="user.isAdmin"
            variant="text"
            color="shamrock"
            class="my-0 pl-4 justify-start"
            block
            to="/admin"
          >
            <v-icon
              start
              size="18"
            >
              mdi-account
            </v-icon>
            <span>Admin zone</span>
          </v-btn>
          <v-btn
            variant="text"
            color="shamrock"
            class="my-0 pl-4 justify-start"
            block
            href="https://climatepositivedesign.com/donate"
            target="_blank"
          >
            <v-icon
              start
              size="18"
            >
              mdi-heart
            </v-icon>
            Donate
          </v-btn>
          <v-btn
            variant="text"
            color="shamrock"
            class="my-0 pl-4 justify-start"
            block
            @click="logoutUser()"
          >
            <v-icon
              start
              size="18"
            >
              mdi-logout
            </v-icon>
            Logout
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
    <v-dialog
      v-model="editAccountDialog"
      max-width="650px"
    >
      <ValidationObserver
        ref="observer"
        v-slot="{ handleSubmit, meta }"
      >
        <v-form
          @submit.prevent="handleSubmit(submitEditedUser)"
        >
          <v-card
            elevation="24"
            style="overflow-x: hidden; border-radius: 4px;"
          >
            <v-card-title class="d-flex justify-center my-5">
              Edit My Account
            </v-card-title>
            <v-card-text>
              <ValidationProvider
                v-slot="{ errors, handleChange }"
                name="Name"
                :model-value="editedUser.name"
                rules="required"
              >
                <v-text-field
                  v-model="editedUser.name"
                  :error-messages="errors"
                  label="Name"
                  @update:model-value="handleChange"
                />
              </ValidationProvider>
              <v-expand-transition>
                <ValidationProvider
                  v-slot="{ errors, handleChange }"
                  name="Country"
                  :model-value="editedUser.country"
                  rules="required"
                >
                  <v-autocomplete
                    v-model="editedUser.country"
                    :items="countries"
                    hide-selected
                    item-title="name"
                    autocomplete="off"
                    placeholder="Country"
                    name="country"
                    :error-messages="errors"
                    return-object
                    @update:model-value="handleChange"
                  />
                </ValidationProvider>
              </v-expand-transition>
              <ValidationProvider
                v-slot="{ errors, handleChange }"
                name="Company"
                :model-value="editedUser.company"
                rules="required"
              >
                <v-text-field
                  v-model="editedUser.company"
                  :error-messages="errors"
                  label="Company"
                  @update:model-value="handleChange"
                />
              </ValidationProvider>
              <ValidationProvider
                v-slot="{ handleChange }"
                name="Organization Type"
                :model-value="editedUser.organizationType"
              >
                <v-select
                  v-model="editedUser.organizationType"
                  :items="organizationTypes"
                  label="Organization Type"
                  @update:model-value="handleChange"
                />
              </ValidationProvider>
            </v-card-text>
            <v-card-actions class="mb-4">
              <v-row style="justify-content: space-evenly;">
                <v-btn
                  color="primary"
                  variant="flat"
                  min-width="150px"
                  size="large"
                  @click="editAccountDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  variant="flat"
                  min-width="150px"
                  size="large"
                  :disabled="!meta.dirty"
                  type="submit"
                >
                  Save
                </v-btn>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-form>
      </ValidationObserver>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, createNamespacedHelpers } from 'vuex'
import countries from '@/views/signIn/countries.json'
import { Form as ValidationObserver, Field as ValidationProvider } from 'vee-validate'
import { ORGANIZATION_TYPES } from '@/helpers/constants'

const { mapGetters: mapAuthGetters, mapActions: mapAuthActions } = createNamespacedHelpers('auth')

export default {
  name: 'UserSettings',
  components: {
    ValidationObserver,
    ValidationProvider
  },
  props: {
    small: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      editAccountDialog: false,
      countries,
      organizationTypes: ORGANIZATION_TYPES,
      editedUser: {
        name: '',
        country: { name: '' },
        company: '',
        organizationType: ''
      }
    }
  },
  computed: {
    ...mapState(['page']),
    ...mapAuthGetters(['user']),
    colors () {
      const colors = {}
      switch (this.page.toolbar) {
        case 'dark':
          colors.bar = 'onyx text-white'
          colors.arrow = 'white'
          break
        case 'light':
          colors.bar = 'leaf text-primary'
          colors.arrow = 'primary'
          break
        default:
          colors.bar = 'white text-primary'
          colors.arrow = 'primary'
          break
      }
      return colors
    },
    onLogout () {
      return `${window.location.origin}`
    }
  },
  methods: {
    ...mapAuthActions(['logoutUser', 'updateUser']),
    editedUserInformation () {
      const { name, company, country, organizationType } = this.user
      this.editedUser = { name, company, country, organizationType }
      this.editAccountDialog = true
    },
    async submitEditedUser () {
      this.updateUser(this.editedUser)
      this.editAccountDialog = false
    }
  }
}
</script>

<style lang="scss" scoped>
.bottom-bar {
  box-shadow: none !important;
}
.v-btn--active {
  font-weight: 600;
}
</style>
